import {
  History,
  LayoutDashboard,
  LayoutTemplate,
  House,
  LayoutGrid,
  Palette,
  Settings,
  ShieldCheck,
  Sparkles,
  SwatchBook,
  type LucideIcon,
  Users,
} from 'lucide-react';
import { WEB_MODULES } from './modules/index.ts';

/**
 * Uma tela do menu (uma funcionalidade): endereço, chave do texto, ícone e a permissão
 * para ver (null = todos). As operações de cada tela (criar, aprovar…) são as permissões
 * `<módulo>.<entidade>.<ação>` conferidas dentro dela e no servidor.
 */
export interface NavItem {
  to: string;
  key: string;
  icon: LucideIcon;
  /** Basta ter uma delas (null = todos). */
  permission: string | readonly string[] | null;
  /** Página inicial do departamento: só fica marcada no menu quando é a tela aberta. */
  overview?: boolean;
}

/** Um módulo do departamento: um grupo de telas. Sem título = telas do dia a dia, no topo. */
export interface NavGroup {
  key: string | null;
  items: NavItem[];
  /** Seção do departamento a que o grupo pertence (só nos departamentos com `sections`). */
  section?: string;
}

/**
 * Seção de um departamento de menu enxuto: uma entrada só no menu, que abre uma página com as
 * telas dos grupos dela (`/<departamento>/<seção>`). O texto de apresentação fica em
 * `departmentHome.sectionAbout.<id>`.
 */
export interface NavSection {
  id: string;
  key: string;
  icon: LucideIcon;
}

/** Um departamento: uma entrada na lateral, com os seus módulos e telas. */
export interface NavArea {
  id: string;
  /** Nome completo: lateral, caminho de navegação e busca. */
  key: string;
  /** Nome curto (lateral recolhida); sem ele, vale o completo. */
  railKey?: string;
  icon: LucideIcon;
  /**
   * Chave do texto de apresentação da página inicial do departamento; sem ela,
   * `departmentHome.about.<id>` (os módulos usam a deles, ADR 0123).
   */
  aboutKey?: string;
  /** Fica fora da lista de departamentos, no rodapé da lateral (Configuração). */
  bottom?: boolean;
  /** Menu enxuto: o menu mostra a visão geral e uma entrada por seção, não cada tela. */
  sections?: NavSection[];
  groups: NavGroup[];
}

/** A página inicial de um departamento; o texto de apresentação fica em `departmentHome.about.<id>`. */
function overview(to: string, key = 'nav.overview'): NavItem {
  return { to, key, icon: LayoutGrid, permission: null, overview: true };
}

/**
 * Lateral única (docs/guia-visual/README.md, "Navegação"; ADR 0012 e ADR 0071).
 * Hierarquia: departamento → módulo (grupo) → funcionalidade (tela) → operação (permissão).
 * Ordem: Início e os departamentos no topo; Configuração no rodapé, com submenu próprio.
 * Os módulos instalados trazem os próprios departamentos (`webModule.areas`, ADR 0123).
 */
const BASE_AREAS: NavArea[] = [
  {
    id: 'home',
    key: 'areas.home',
    icon: House,
    groups: [
      {
        key: null,
        // Ajuda, as configurações da pessoa e Sair ficam no rodapé da lateral.
        items: [{ to: '/', key: 'nav.home', icon: House, permission: null }],
      },
    ],
  },
  {
    // Configuração: não fica entre os departamentos. Abre pelo rodapé da lateral e tem o próprio
    // submenu (ConfigLayout), com a visão geral e as telas de administração.
    id: 'settings',
    key: 'areas.settings',
    icon: Settings,
    bottom: true,
    groups: [
      {
        key: null,
        items: [
          overview('/settings', 'nav.settingsOverview'),
          {
            to: '/settings/platform',
            key: 'nav.platform',
            icon: Palette,
            permission: 'platform.settings.manage',
          },
          {
            to: '/settings/style',
            key: 'nav.style',
            icon: LayoutTemplate,
            permission: 'platform.settings.manage',
          },
          {
            to: '/settings/design',
            key: 'nav.design',
            icon: SwatchBook,
            permission: 'platform.settings.manage',
          },
          {
            to: '/settings/patterns',
            key: 'nav.patterns',
            icon: LayoutDashboard,
            permission: 'platform.settings.manage',
          },
          { to: '/settings/ai', key: 'nav.ai', icon: Sparkles, permission: 'platform.ai.chat' },
          { to: '/settings/users', key: 'nav.users', icon: Users, permission: 'iam.user.read' },
          {
            to: '/settings/groups',
            key: 'nav.roles',
            icon: ShieldCheck,
            permission: 'iam.role.read',
          },
          {
            to: '/settings/audit',
            key: 'nav.audit',
            icon: History,
            permission: ['audit.log.read', 'audit.access.read'],
          },
        ],
      },
    ],
  },
];

/**
 * O menu inteiro: os departamentos da base e, antes da Configuração, os dos módulos instalados
 * (ADR 0123), na ordem das dependências.
 */
export const AREAS: NavArea[] = [
  ...BASE_AREAS.filter((a) => !a.bottom),
  ...WEB_MODULES.flatMap((m) => m.areas),
  ...BASE_AREAS.filter((a) => a.bottom),
];

/** A chave do texto de apresentação da página inicial do departamento. */
export function aboutKeyOf(area: NavArea): string {
  return area.aboutKey ?? `departmentHome.about.${area.id}`;
}

export function itemsOf(area: NavArea): NavItem[] {
  return area.groups.flatMap((g) => g.items);
}

/** As telas de verdade do departamento, sem a página inicial. */
export function screensOf(area: NavArea): NavItem[] {
  return itemsOf(area).filter((i) => !i.overview);
}

/**
 * O ícone do departamento abre um menu na moldura (a coluna do Trilho e coluna, a lateral que se
 * abre na faixa recolhida) em vez de levar à página? Início, departamento em preparação e as áreas
 * do rodapé (Configuração, que tem o submenu na própria página e nunca abre coluna) levam direto à
 * página. Regra única para toda moldura: um botão que só abre menu, sem menu para abrir, não faz nada.
 */
export function opensMenu(area: NavArea): boolean {
  return area.id !== 'home' && !area.bottom && screensOf(area).length > 0;
}

/** Endereço da página de uma seção: `/<departamento>/<seção>`. */
export function sectionPath(area: NavArea, section: NavSection): string {
  return `/${area.id}/${section.id}`;
}

/** As seções que aparecem: só as que têm algum grupo (com `visibleAreas`, alguma tela liberada). */
export function sectionsOf(area: NavArea): NavSection[] {
  return (area.sections ?? []).filter((s) => area.groups.some((g) => g.section === s.id));
}

/** Os grupos de telas de uma seção. */
export function groupsOf(area: NavArea, section: NavSection): NavGroup[] {
  return area.groups.filter((g) => g.section === section.id);
}

/** O departamento e a seção cuja página é este endereço. */
export function sectionAt(
  areas: NavArea[],
  pathname: string,
): { area: NavArea; section: NavSection } | undefined {
  for (const area of areas)
    for (const section of area.sections ?? [])
      if (sectionPath(area, section) === pathname) return { area, section };
  return undefined;
}

/**
 * A seção que fica marcada no menu enxuto: a da própria página da seção ou a da tela aberta.
 * Na visão geral (e fora do departamento), nenhuma.
 */
export function activeSectionId(area: NavArea, pathname: string): string | undefined {
  const page = sectionAt([area], pathname);
  if (page) return page.section.id;
  const { group, item } = locate([area], pathname);
  return item && !item.overview ? group?.section : undefined;
}

/**
 * Os departamentos com só as telas que a pessoa pode ver. Departamento com telas, mas
 * nenhuma liberada, some. Departamento ainda em preparação (sem telas) aparece para todos.
 */
export function visibleAreas(allowed: (permission: string) => boolean): NavArea[] {
  return AREAS.filter((area) => {
    const screens = screensOf(area);
    return screens.length === 0 || screens.some((i) => permitted(i, allowed));
  }).map((area) => ({
    ...area,
    groups: area.groups
      .map((g) => ({
        ...g,
        items: g.items.filter((i) => permitted(i, allowed)),
      }))
      .filter((g) => g.items.length > 0),
  }));
}

function permitted(item: NavItem, allowed: (permission: string) => boolean): boolean {
  if (item.permission === null) return true;
  const list = typeof item.permission === 'string' ? [item.permission] : item.permission;
  return list.some(allowed);
}

function matches(item: NavItem, pathname: string): boolean {
  return item.to === '/'
    ? pathname === '/'
    : pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/**
 * Telas internas: não ficam no menu porque são abertas de dentro de outra tela (o menu mostra os
 * processos, não ações repetidas). O caminho mostra a tela de origem e a própria tela, e o
 * departamento certo fica marcado. Ex.: Módulo › Registros › Novo registro.
 */
export interface InnerScreen {
  match: (pathname: string) => boolean;
  /** Endereço do item do menu de onde a tela é aberta. */
  parent: string;
  /** Nome da tela no caminho (chave de tradução). */
  key: string;
}
export const INNER_SCREENS: readonly InnerScreen[] = [
  // Ex.: { match: (p) => /^\/modulo\/[0-9a-f-]{36}$/i.test(p), parent: '/modulo', key: 'modulo.detail.crumb' },
];

/** A tela interna do endereço, com o item de origem que a pessoa vê no menu. */
function innerScreen(areas: NavArea[], pathname: string) {
  const inner = INNER_SCREENS.find((s) => s.match(pathname));
  if (!inner) return undefined;
  for (const area of areas)
    for (const group of area.groups)
      for (const item of group.items)
        if (item.to === inner.parent) return { area, group, item, inner };
  return undefined;
}

/** O departamento, o módulo e a tela do endereço atual (a tela de endereço mais longo vence). */
export function locate(
  areas: NavArea[],
  pathname: string,
): {
  area: NavArea | undefined;
  group: NavGroup | undefined;
  item: NavItem | undefined;
  inner?: InnerScreen;
} {
  const inside = innerScreen(areas, pathname);
  if (inside) return inside;
  let found: { area: NavArea; group: NavGroup; item: NavItem } | undefined;
  for (const area of areas)
    for (const group of area.groups)
      for (const item of group.items)
        if (matches(item, pathname) && (!found || item.to.length > found.item.to.length))
          found = { area, group, item };
  return found ?? { area: areas[0], group: undefined, item: undefined };
}

/**
 * O item do menu que fica marcado no endereço atual: só um, o de endereço mais longo (a mesma regra
 * do caminho). Assim uma tela em /a/b não acende também a tela /a.
 */
export function activeItemTo(areas: NavArea[], pathname: string): string | undefined {
  const item = locate(areas, pathname).item;
  // A visão geral (e o Início) só fica marcada na própria página, como sempre foi.
  if (!item || ((item.overview === true || item.to === '/') && item.to !== pathname))
    return undefined;
  return item.to;
}

/**
 * O que a lateral única abre sozinha no endereço atual (ADR 0071): o departamento da tela e, nos
 * departamentos com módulos, o módulo dela. No Início (e nos itens soltos do topo), nenhum.
 */
export function menuTarget(
  areas: NavArea[],
  pathname: string,
): { areaId: string | undefined; groupKey: string | undefined } {
  const { area, group, item } = locate(areas, pathname);
  if (!area || !item || area.id === 'home') return { areaId: undefined, groupKey: undefined };
  // Nas seções (Administração) o menu não abre módulos: a seção marcada basta.
  return { areaId: area.id, groupKey: area.sections ? undefined : (group?.key ?? undefined) };
}

/** A tela do menu com este endereço, se a pessoa pode vê-la (atalhos do Início usam a mesma regra). */
export function visibleItem(areas: NavArea[], to: string): NavItem | undefined {
  for (const area of areas) for (const item of itemsOf(area)) if (item.to === to) return item;
  return undefined;
}

export interface Crumb {
  key: string;
  /** Endereço para voltar; sem endereço = onde a pessoa está (ou um módulo, que não é tela). */
  to?: string;
}

/**
 * Caminho "Início › Departamento › Módulo › Tela" do endereço atual. Nos departamentos com seções:
 * "Início › Departamento › Seção › Grupo (quando a seção tem mais de um) › Tela", com a seção
 * levando à página dela.
 */
export function breadcrumbs(areas: NavArea[], pathname: string): Crumb[] {
  const { area, group, item, inner } = locate(areas, pathname);
  if (!area || !item || item.to === '/') return [];
  // A primeira tela é a porta de entrada do departamento (a página inicial).
  const home = itemsOf(area)[0]!;
  const crumbs: Crumb[] = [
    { key: 'nav.home', to: '/' },
    home.to === pathname ? { key: area.key } : { key: area.key, to: home.to },
  ];
  if (item === home) {
    // A página de uma seção fica "dentro" da visão geral pelo endereço (/system/platform).
    const page = sectionAt([area], pathname);
    if (page) crumbs.push({ key: page.section.key });
    return crumbs;
  }
  const section = area.sections?.find((s) => s.id === group?.section);
  if (section) crumbs.push({ key: section.key, to: sectionPath(area, section) });
  if (group?.key && group.key !== section?.key) crumbs.push({ key: group.key });
  crumbs.push(item.to === pathname ? { key: item.key } : { key: item.key, to: item.to });
  if (inner) crumbs.push({ key: inner.key });
  return crumbs;
}
